import { useMemo } from 'react';
import { DEVICE_TYPE, useDeviceType } from '@libs/common-next';

type UseResponsiveDisplay = {
    showFirstEllipsis: boolean;
    showLastEllipsis: boolean;
    pagesToShowBetweenEllipsis: number[];
};

type UseResponsiveDisplayProps = {
    pages: number[];
    currentRefinement: number;
    nbPages: number;
};

const MOBILE_PARAMS = {
    MAX_PAGES_ITEMS_WITH_ONE_ELLIPSIS: 3,
    MAX_PAGE_ITEMS_BETWEEN_ELLIPSIS: 1,
    MAX_PAGES_ITEMS_TO_DISPLAY: 5,
    FIRST_ELLIPSIS_THRESHOLD: 2,
    LAST_ELLIPSIS_THRESHOLD: 3,
};

const DESKTOP_PARAMS = {
    MAX_PAGES_ITEMS_WITH_ONE_ELLIPSIS: 5,
    MAX_PAGE_ITEMS_BETWEEN_ELLIPSIS: 3,
    MAX_PAGES_ITEMS_TO_DISPLAY: 6,
    FIRST_ELLIPSIS_THRESHOLD: 3,
    LAST_ELLIPSIS_THRESHOLD: 4,
};

export const useResponsiveDisplay = ({
    currentRefinement,
    pages,
    nbPages,
}: UseResponsiveDisplayProps): UseResponsiveDisplay => {
    const isMobile = useDeviceType() === DEVICE_TYPE.MOBILE;

    const params = isMobile ? MOBILE_PARAMS : DESKTOP_PARAMS;
    const {
        MAX_PAGES_ITEMS_TO_DISPLAY,
        FIRST_ELLIPSIS_THRESHOLD,
        MAX_PAGES_ITEMS_WITH_ONE_ELLIPSIS,
        MAX_PAGE_ITEMS_BETWEEN_ELLIPSIS,
        LAST_ELLIPSIS_THRESHOLD,
    } = params;

    const showSomeEllipsis = nbPages > MAX_PAGES_ITEMS_TO_DISPLAY;
    const showFirstEllipsis =
        showSomeEllipsis && currentRefinement > FIRST_ELLIPSIS_THRESHOLD;
    const showLastEllipsis =
        showSomeEllipsis &&
        nbPages - currentRefinement > LAST_ELLIPSIS_THRESHOLD;

    const pagesToShowBetweenEllipsis = useMemo(() => {
        const pageItemsToDisplay: number[] = [];

        let startIndex;
        let endIndex;
        if (showFirstEllipsis && showLastEllipsis) {
            startIndex = isMobile ? currentRefinement : currentRefinement - 1;
            endIndex = startIndex + MAX_PAGE_ITEMS_BETWEEN_ELLIPSIS;
        } else if (showFirstEllipsis) {
            startIndex = nbPages - MAX_PAGES_ITEMS_WITH_ONE_ELLIPSIS;
            endIndex = startIndex + MAX_PAGES_ITEMS_WITH_ONE_ELLIPSIS;
        } else {
            startIndex = 0;
            endIndex = startIndex + MAX_PAGES_ITEMS_WITH_ONE_ELLIPSIS;
        }

        for (let index = startIndex; index < endIndex; index++) {
            pageItemsToDisplay.push(index);
        }

        return pageItemsToDisplay;
    }, [
        currentRefinement,
        nbPages,
        isMobile,
        showFirstEllipsis,
        showLastEllipsis,
        MAX_PAGES_ITEMS_WITH_ONE_ELLIPSIS,
        MAX_PAGE_ITEMS_BETWEEN_ELLIPSIS,
    ]);

    return {
        showFirstEllipsis,
        showLastEllipsis,
        pagesToShowBetweenEllipsis: showSomeEllipsis
            ? pagesToShowBetweenEllipsis
            : pages,
    };
};
